import type { ReactNode } from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AnnouncementBell from '@/components/common/AnnouncementBell';

interface PageShellProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  showAnnouncements?: boolean;
  reserveBottomNav?: boolean;
  children: ReactNode;
}

interface SectionHeaderProps {
  title: string;
  detail?: ReactNode;
  action?: ReactNode;
}

interface MetricCardProps {
  label: string;
  value: ReactNode;
  helper?: ReactNode;
  icon?: ReactNode;
  tone?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}

export function PageShell({
  eyebrow,
  title,
  subtitle,
  action,
  showAnnouncements = false,
  reserveBottomNav = true,
  children,
}: PageShellProps) {
  const bottomPadding = reserveBottomNav
    ? 'calc(var(--app-bottom-obstruction) + var(--app-page-bottom-padding))'
    : 'calc(var(--app-safe-area-bottom) + var(--app-page-bottom-padding))';

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100%',
        pl: {
          xs: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-left))',
          sm: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-left))',
        },
        pr: {
          xs: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-right))',
          sm: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-right))',
        },
        pt: {
          xs: 'var(--app-page-top-padding)',
          sm: 'var(--app-page-top-padding)',
        },
        pb: {
          xs: bottomPadding,
          sm: bottomPadding,
        },
      }}
    >
      {showAnnouncements && (
        <Box
          sx={{
            position: 'absolute',
            top: {
              xs: 'calc(var(--app-page-top-padding) + 6px)',
              sm: 'calc(var(--app-page-top-padding) + 13px)',
            },
            right: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-right))',
            zIndex: 2,
          }}
        >
          <AnnouncementBell />
        </Box>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          {eyebrow && (
            <Typography
              variant="overline"
              color="primary.main"
              sx={{ display: 'block', fontWeight: 800, lineHeight: 1.4 }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography variant="h4" sx={{ fontSize: { xs: '2rem', sm: '2.4rem' } }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 620 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
      {children}
    </Box>
  );
}

export function SectionHeader({ title, detail, action }: SectionHeaderProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ mt: 2.5, mb: 1.25 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6">{title}</Typography>
        {detail && (
          <Typography variant="body2" color="text.secondary">
            {detail}
          </Typography>
        )}
      </Box>
      {action}
    </Stack>
  );
}

export function MetricCard({ label, value, helper, icon, tone = 'primary' }: MetricCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              {label}
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5 }}>
              {value}
            </Typography>
          </Box>
          {icon && (
            <Box
              sx={{
                width: 38,
                height: 38,
                minWidth: 38,
                minHeight: 38,
                flex: '0 0 38px',
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                color: `${tone}.main`,
                backgroundColor: (theme) => alpha(theme.palette[tone].main, 0.16),
              }}
            >
              {icon}
            </Box>
          )}
        </Stack>
        {helper && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
